import type { CSSProperties, ReactNode } from 'react';
import { cx } from './cx';

export interface SkeletonProps {
  width?: CSSProperties['width'];
  height?: CSSProperties['height'];
  circle?: boolean;
  className?: string;
}

/** A placeholder shape. Always hidden from assistive technology — wrap groups in SkeletonGroup. */
export function Skeleton({
  width = '100%',
  height = '0.875rem',
  circle,
  className,
}: SkeletonProps) {
  return (
    <span
      aria-hidden="true"
      data-skeleton=""
      className={cx(
        'block animate-pulse bg-surface-sunken',
        circle ? 'rounded-full' : 'rounded-ui',
        className,
      )}
      style={{ width, height }}
    />
  );
}

/** Lines of text; the last one shorter, like a paragraph. */
export function SkeletonText({ lines = 3, className }: { lines?: number; className?: string }) {
  return (
    <span aria-hidden="true" className={cx('flex flex-col gap-2', className)}>
      {Array.from({ length: lines }, (_, i) => (
        <Skeleton key={i} width={i === lines - 1 && lines > 1 ? '60%' : '100%'} />
      ))}
    </span>
  );
}

/**
 * Loading region: announces `label` once (role=status) and marks the area busy, while the
 * placeholder shapes stay invisible to screen readers.
 */
export function SkeletonGroup({
  label = 'Loading…',
  children,
  className,
}: {
  label?: string;
  children: ReactNode;
  className?: string;
}) {
  return (
    <div aria-busy="true" className={className}>
      <span role="status" className="sr-only">
        {label}
      </span>
      {children}
    </div>
  );
}

/** Card-shaped placeholder used while a panel loads. */
export function SkeletonCard({ lines = 4 }: { lines?: number }) {
  return (
    <div
      aria-hidden="true"
      className="border border-border-widget bg-surface"
      data-testid="skeleton-card"
    >
      <div className="flex h-widget-header items-center gap-2 border-b border-border-widget bg-surface-raised px-3">
        <Skeleton width="1rem" height="1rem" circle />
        <Skeleton width="40%" />
      </div>
      <div className="p-3">
        <SkeletonText lines={lines} />
      </div>
    </div>
  );
}
